import {
  ChangeDetectionStrategy,
  Compiler,
  Component,
  ComponentRef,
  computed,
  effect,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {RoutesQueryParamsEnum} from '../../../../enums/RoutesEnums';
import {SearchHelperService} from '../../../../services/search-helper.service';
import {SearchReloadService} from '../../../../services/search/search-reload.service';
import {SearchService} from '../../../../services/search/search.service';
import {ToolsService} from '../../../../services/tools.service';
import {SearchState} from '../../../../types/SearchState';
import {SearchMainComponentEnum} from '../../enums/search-main-component.enum';
import {LocalStateService} from '../../services/local-state.service';
import {SearchRedirectsService} from '../../services/search-redirects.service';
import {DefaultValuesService} from '../../../../services/default-values.service';
import {SearchMetaTagsService} from '../../services/search-meta-tags.service';

@Component({
  selector: 'app-search-main-component-selector',
  templateUrl: './search-main-component-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchMainComponentSelectorComponent implements OnInit, OnDestroy {
  public isRedirected = signal<boolean>(false);
  private subscriptions: Subscription[] = [];
  private searchService = inject(SearchService);

  public state = this.searchService.searchState;
  private emptyContentPage = signal<boolean>(false);
  public mainComponent = computed<SearchMainComponentEnum>(() => {
    if (
      !this.tools.isNil(this.state()) &&
      this.tools.isEqual(this.state(), this.defaultValuesService.getDefaultSearchState()) === false
    ) {
      if (this.searchHelper.shouldDisplayServiceTypePage(this.state()) === true && this.emptyContentPage() === false) {
        return SearchMainComponentEnum.SERVICE;
      } else if (
        this.searchHelper.shouldDisplaySymptomPage(this.state()) === true &&
        this.emptyContentPage() === false
      ) {
        return SearchMainComponentEnum.SYMPTOM;
      } else {
        return SearchMainComponentEnum.SERP;
      }
    } else {
      return null;
    }
  });

  @ViewChild('dynamicComponentContainer', {read: ViewContainerRef, static: true})
  private dynamicComponentContainer!: ViewContainerRef;
  private componentRef: ComponentRef<any>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private searchRedirects: SearchRedirectsService,
    private localState: LocalStateService,
    private tools: ToolsService,
    // private layoutService: LayoutService,
    private searchHelper: SearchHelperService,
    private reloadService: SearchReloadService,
    private defaultValuesService: DefaultValuesService,
    private searchMeta: SearchMetaTagsService,
    private injector: Injector,
    private compiler: Compiler,
  ) {
    // effect(
    //   () => {
    //     if (this.mainComponent() === SearchMainComponentEnum.SERP) {
    //       this.layoutService.setShouldUseNarrowMainContainer(false);
    //     } else if (this.mainComponent() !== null) {
    //       this.layoutService.setShouldUseNarrowMainContainer(true);
    //     }
    //   },
    //   {allowSignalWrites: true},
    // );

    effect(() => {
      if (this.mainComponent() && this.state()) {
        this.loadAppropriateModule();
      }
    });
  }

  ngOnInit() {
    this.setListeners();
    this.onRouteChanged(true);
  }

  public ngOnDestroy() {
    if (this.componentRef) {
      this.componentRef.destroy();
    }
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public onEmptyContentPage($event: boolean): void {
    if ($event === true) {
      const newState = this.tools.clone(this.state());
      newState.forceSearchResults = true;
      this.searchService.updateSearchState(newState);
      this.emptyContentPage.set(true);
    }
  }

  private setListeners(): void {
    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.onRouteChanged(false);
        }
      }),
    );
  }

  private onRouteChanged(isInitialLoad: boolean): void {
    this.emptyContentPage.set(false);

    if (isInitialLoad === false) {
      this.searchService.resetSearchDataSignals();
    }

    if (this.route.snapshot.queryParams[RoutesQueryParamsEnum.NEARBY] === '1') {
      this.searchService.triggerLocationSearch.set(true);
    }

    this.reloadService.updateParams(this.route.snapshot.params, this.route.snapshot.queryParams);
    const state: SearchState = this.localState.UpdateState(
      this.route.snapshot.params,
      this.route.snapshot.queryParams,
      this.router.routerState.snapshot.url,
    );
    if (isInitialLoad === true) {
      this.isRedirected.set(
        this.searchRedirects.conditionalRedirectOnBadStateParams(
          state,
          this.route.snapshot.params,
          this.route.snapshot.queryParams,
        ),
      );
    }

    if (this.isRedirected() === false || (state && state.isRedirectToStateBasedUrlRequired !== true)) {
      this.searchService.updateSearchState(state);
      this.searchService.resetWidgetState();
    }
    this.searchMeta.updateCanonicalTag(state);
  }

  private async loadAppropriateModule() {
    let componentRef: ComponentRef<any>;
    this.dynamicComponentContainer.clear();

    if (this.mainComponent() === SearchMainComponentEnum.SERP) {
      const {SerpV2Module} = await import('../../modules/serp-v2/serp-v2.module');
      const moduleFactory = await this.compiler.compileModuleAsync(SerpV2Module);
      const moduleRef = moduleFactory.create(this.injector);
      const serpComponentFactory = moduleRef.instance.resolveComponentFactory();
      componentRef = this.dynamicComponentContainer.createComponent(serpComponentFactory);
      componentRef.setInput('isConditionalRedirected', this.isRedirected());
    } else if (
      this.mainComponent() === SearchMainComponentEnum.SERVICE ||
      this.mainComponent() === SearchMainComponentEnum.SYMPTOM
    ) {
      const {SearchContentPagesV2Module} = await import(
        '../../modules/search-content-pages-v2/search-content-pages-v2.module'
      );

      const moduleFactory = await this.compiler.compileModuleAsync(SearchContentPagesV2Module);
      const moduleRef = moduleFactory.create(this.injector);

      const serpContentPageComponentFactory = moduleRef.instance.resolveComponentFactory();

      componentRef = this.dynamicComponentContainer.createComponent(serpContentPageComponentFactory);

      componentRef.setInput('state', this.state());
      componentRef.instance.onEmptyContentPage.subscribe((event: boolean) => this.onEmptyContentPage(event));
    }

    this.componentRef = componentRef;
  }
}
